import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Plus, Loader2, X, ChevronRight, PlusCircle, Star, Clock, Heart, UtensilsCrossed, Coffee, Pill, Flame, BookOpen } from "lucide-react";
import { translateFoodName } from "@/i18n/foodTranslations";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import FoodDetailPanel from "./FoodDetailPanel";
import RestaurantImportModal from "./RestaurantImportModal";  // iter537
import FoodReportButton from "./FoodReportButton";  // iter721 — user reports bad food data
import { computeDil } from "@/lib/dilFormula";

/** iter420.2 / iter421.7 — Save searched food to Health Intelligence favorites collection.
 *  Hardened against missing food.name, missing scaled fields, computeDil NaN. */
function SaveFoodToHIButton({ food, scaled, bg, isDark }) {
  const { API } = useAuth();
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  const handleClick = async () => {
    if (saving || saved) return;
    setSaving(true);
    try {
      // Robust extraction (food may have name_bg/name_en/name)
      const rawName = food?.name || food?.name_bg || food?.name_en || food?.label || "Храна";
      const translatedName = (() => {
        try { return translateFoodName(rawName, bg ? "bg" : "en") || rawName; }
        catch { return rawName; }
      })();
      const p  = Number(scaled?.protein) || 0;
      const c  = Number(scaled?.carbs)   || 0;
      const fi = Number(scaled?.fiber)   || 0;
      const fa = Number(scaled?.fat)     || 0;
      const kc = Number(scaled?.calories) || 0;
      const dilRaw = computeDil(p, c, fi);
      const dil = Number.isFinite(dilRaw) ? dilRaw : 0;
      const antiInflam = fi >= 5 && dil <= 15;
      await axios.post(`${API}/health-intelligence/snapshot`, {
        snapshot_type: "favorite_food",
        title: String(translatedName).slice(0, 200) || "Храна",
        summary: bg
          ? `Любима храна — DIL ${dil.toFixed(1)}g, фибри ${fi.toFixed(0)}g`
          : `Favorite food — DIL ${dil.toFixed(1)}g, fiber ${fi.toFixed(0)}g`,
        payload: {
          food_name: rawName,
          food_source: food?.source || "unknown",
          dil: Number(dil.toFixed(1)),
          protein_g: p, carbs_g: c, fat_g: fa, fiber_g: fi,
          calories: kc,
          anti_inflammatory: antiInflam,
          source: "food_search",
        },
        tags: [fi >= 5 ? "high-fiber" : null, dil <= 10 ? "low-insulin" : null].filter(Boolean),
      });
      setSaved(true);
      toast.success(bg ? "Запазено в Health Intelligence ❤" : "Saved to Health Intelligence ❤");
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[SaveFoodToHI] failed:", e?.response?.data || e?.message || e);
      const detail = e?.response?.data?.detail;
      const detailMsg = Array.isArray(detail) ? detail.map((d) => d?.msg).filter(Boolean).join("; ") : detail;
      toast.error(detailMsg || e?.message || (bg ? "Грешка при запазване" : "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={saving || saved}
      variant="outline"
      className={`w-full h-9 mt-2 text-xs font-medium rounded-xl ${
        saved
          ? (isDark ? "bg-rose-500/20 text-rose-300 border-rose-500/40" : "bg-rose-50 text-rose-700 border-rose-300")
          : (isDark ? "border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10" : "border-indigo-300 text-indigo-700 hover:bg-indigo-50")
      }`}
      data-testid="food-search-save-to-hi"
    >
      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> :
       <Heart className={`w-3.5 h-3.5 mr-1.5 ${saved ? "fill-current" : ""}`} />}
      {saved
        ? (bg ? "Запазена в любими" : "Saved to favorites")
        : (bg ? "Запази в Health Intelligence" : "Save to Health Intelligence")}
    </Button>
  );
}

const FOOD_CATEGORIES = [
  { id: "all", icon: Search, label: "Всички", labelEn: "All" },
  { id: "favorites", icon: Heart, label: "Любими", labelEn: "Favorites", color: "#EF4444" },
  { id: "recent", icon: Clock, label: "Скорошни", labelEn: "Recent", color: "#F59E0B" },
  // iter536 — Restaurants tab (между Скорошни и Плодове, per user spec)
  { id: "restaurants", icon: null, label: "Ресторанти", labelEn: "Restaurants", emoji: "\uD83C\uDF7D" },
  { id: "fruits", icon: null, label: "Плодове", labelEn: "Fruits", emoji: "\uD83C\uDF4E" },
  { id: "vegetables", icon: null, label: "Зеленчуци", labelEn: "Vegetables", emoji: "\uD83E\uDD66" },
  { id: "meat_fish", icon: null, label: "Месо и риба", labelEn: "Meat & Fish", emoji: "\uD83C\uDF57" },
  { id: "dairy", icon: null, label: "Млечни", labelEn: "Dairy", emoji: "\uD83E\uDDC0" },
  { id: "grains", icon: null, label: "Зърнени", labelEn: "Grains", emoji: "\uD83C\uDF5E" },
  { id: "legumes", icon: null, label: "Бобови", labelEn: "Legumes", emoji: "\uD83E\uDED8" },
  { id: "nuts", icon: null, label: "Ядки", labelEn: "Nuts", emoji: "\uD83E\uDD5C" },
  { id: "beverage", icon: Coffee, label: "Напитки", labelEn: "Beverages", color: "#6366F1" },
  { id: "spice", icon: Flame, label: "Подправки", labelEn: "Spices", color: "#F97316" },
  // iter358 — "Добавки" tab removed per user feedback: the supplement section
  // contained items mis-categorised as foods (with macros) which confused the
  // DIL/UIS/NIS calculations. Supplements live in their own log now.
  { id: "custom", icon: PlusCircle, label: "Мои храни", labelEn: "Custom", color: "#0EA5E9" },
];

const COOKED_TOKENS_BG = ["варен", "печен", "пържен", "задушен", "приготв", "грилован", "термично"];
const COOKED_TOKENS_EN = ["cooked", "boiled", "baked", "roasted", "grilled", "fried", "steamed", "broiled", "braised", "sauteed", "stewed", "poached", "prepared", "moist heat", "dry heat"];
const RAW_TOKENS_BG = ["суров", "пресн", "необработ"];
const RAW_TOKENS_EN = [" raw", ", raw", "raw,", "(raw", "uncooked"];
function _foodName(f) { return ((f.name || "") + " " + (f.name_bg || "")).toLowerCase(); }

/** iter965 — Locale-aware display of a Nutrami/food row.
 *  Bulgarian users see the curated `name_bg`; other locales get `name_en`
 *  and fall back to whatever is available. Never concatenates the two. */
function _pickNutramiName(food, language) {
  const bg = (food?.name_bg || "").trim();
  const en = (food?.name_en || "").trim();
  const fallback = (food?.name || food?.food_name || "").trim();
  if (language === "bg") return bg || en || fallback;
  return en || bg || fallback;
}

/** iter967 — Secondary (English/USDA) name shown as a small subtitle
 *  beneath the primary display name. Rationale (per user):
 *   • BG-market curated Nutrami names sometimes differ from the USDA
 *     English source (e.g., "Малини" ↔ "Raspberries, raw"), so an
 *     English/foreign-user needs the English label to recognise the
 *     food when they land on a page in BG locale.
 *   • For USDA rows in BG locale, we already translate the head-word
 *     ("Ябълка (raw, with skin)"); showing the raw USDA description
 *     verbatim underneath gives transparency + searchability.
 *
 *  iter969 — For Nutrami rows we now prefer `source_usda_name` (the
 *  full authoritative USDA English description backfilled from the
 *  linked `source_fdc_id`) over the market-tuned `name_en`, so the
 *  subtitle is guaranteed to match the USDA source row 1:1.
 *
 *  Returns the string to render, or "" when no meaningful subtitle
 *  exists (avoid duplicating the primary line). */
function _foodSubtitle(food, language, primaryDisplay) {
  const primary = (primaryDisplay || "").trim().toLowerCase();
  let candidate = "";
  if (food?.source === "nutrami") {
    if (language === "bg") {
      // Under a Cyrillic primary — prefer the USDA source description,
      // fall back to `name_en` (market-tuned), then empty.
      candidate = (food.source_usda_name || food.name_en || "");
    } else {
      // Under an English/foreign primary — show the BG label so a
      // Bulgarian visitor still recognises the food.
      candidate = (food.name_bg || "");
    }
  } else {
    // USDA / other — raw original English description
    candidate = (food.name || food.food_name || "");
  }
  candidate = (candidate || "").trim();
  if (!candidate) return "";
  if (candidate.toLowerCase() === primary) return "";
  return candidate;
}
function _isCookedFood(f) {
  const n = _foodName(f);
  return COOKED_TOKENS_BG.some(t => n.includes(t)) || COOKED_TOKENS_EN.some(t => n.includes(t));
}
function _isRawFood(f) {
  const n = _foodName(f);
  return RAW_TOKENS_BG.some(t => n.includes(t)) || RAW_TOKENS_EN.some(t => n.includes(t));
}

const DIETARY_FILTERS = [
  // iter606 — preparation filter (excludes raw / cooked items by name token)
  { id: "cooked_only", labelBg: "Само готвена", labelEn: "Cooked only", color: "#F59E0B", test: (f) => !_isRawFood(f) },
  { id: "raw_only", labelBg: "Само сурова", labelEn: "Raw only", color: "#84CC16", test: (f) => !_isCookedFood(f) },
  { id: "high_protein", labelBg: "Високо протеин", labelEn: "High Protein", color: "#10B981", test: (f) => (f.per100g?.protein || 0) >= 15 },
  { id: "low_carb", labelBg: "Ниски въглехидрати", labelEn: "Low Carb", color: "#0EA5E9", test: (f) => (f.per100g?.carbs || 0) <= 10 },
  { id: "high_fiber", labelBg: "Богати на фибри", labelEn: "High Fiber", color: "#22C55E", test: (f) => (f.per100g?.fiber || 0) >= 5 },
  { id: "low_cal", labelBg: "Ниска калорийност", labelEn: "Low Cal", color: "#EF4444", test: (f) => (f.per100g?.calories || 0) <= 100 },
];

// Source labels like Cronometer — shows data provenance for each food result
const SOURCE_META = {
  custom:    { bg: "Моя",       en: "Mine",     cls: "bg-[#0EA5E9]/15 text-[#0EA5E9] ring-1 ring-[#0EA5E9]/30" },
  nutrami:      { bg: "Nutrami",bg2: "Nutrami",en: "Nutrami",     cls: "bg-emerald-600/20 text-emerald-700 ring-1 ring-emerald-600/40 font-bold", title_bg: "Курирана храна (Nutrami)", title_en: "Curated (Nutrami)" },
  bg_canonical: { bg: "БГ",     en: "BG",       cls: "bg-emerald-600/20 text-emerald-700 ring-1 ring-emerald-600/40 font-bold", title_bg: "Курирана българска храна", title_en: "Curated Bulgarian food" },
  favorite:  { bg: "Любима",    en: "Favorite", cls: "bg-amber-500/15 text-amber-600 ring-1 ring-amber-500/30" },
  recent:    { bg: "Скорошна",  en: "Recent",   cls: "bg-gray-500/15 text-gray-600 ring-1 ring-gray-500/30" },
  local:     { bg: "NTR",       en: "NTR",      cls: "bg-emerald-500/15 text-emerald-600 ring-1 ring-emerald-500/30", title_bg: "Nutramilo верифицирана храна", title_en: "Nutramilo verified food" },
  usda_db:   { bg: "USDA",      en: "USDA",     cls: "bg-blue-500/15 text-blue-600 ring-1 ring-blue-500/30", title_bg: "USDA FoodData Central", title_en: "USDA FoodData Central" },
  usda_api:  { bg: "USDA◡",     en: "USDA◡",    cls: "bg-blue-500/10 text-blue-500 ring-1 ring-blue-500/20", title_bg: "USDA API (на живо)", title_en: "USDA API (live)" },
  usda:      { bg: "USDA",      en: "USDA",     cls: "bg-blue-500/15 text-blue-600 ring-1 ring-blue-500/30" },
  edamam:    { bg: "Edamam",    en: "Edamam",   cls: "bg-orange-500/15 text-orange-600 ring-1 ring-orange-500/30" },
  supplements: { bg: "Добавка",  en: "Supplement", cls: "bg-pink-500/15 text-pink-600 ring-1 ring-pink-500/30", title_bg: "Витамин / минерал / добавка", title_en: "Vitamin / mineral / supplement" },
};

function SourceBadge({ source, bg }) {
  const m = SOURCE_META[source];
  if (!m) return null;
  return (
    <span
      className={`inline-flex items-center px-1 py-px rounded text-[9px] font-semibold flex-shrink-0 ${m.cls}`}
      title={bg ? (m.title_bg || m.bg) : (m.title_en || m.en)}
      data-testid={`food-source-${source}`}
    >
      {bg ? m.bg : m.en}
    </span>
  );
}

export function FoodSearchOverlay({
  isDark, bg, language,
  activeMeal, searchQuery, setSearchQuery, onSearch,
  searching, searchResults, translatedQuery,
  selectedFood, setSelectedFood,
  amount, setAmount, adding, onAddFood, onClose,
  onAddCustomFood,
  // iter300 — optional meal-type switcher (rendered if setActiveMealType + mealTypes provided)
  activeMealType, setActiveMealType, mealTypes,
  // iter327N — circadian time-of-consumption (HH:MM)
  logTime, setLogTime,
}) {
  const { API } = useAuth();
  const [activeTab, setActiveTab] = useState("all");
  const [favorites, setFavorites] = useState([]);
  const [recents, setRecents] = useState([]);
  const [customFoods, setCustomFoods] = useState([]);
  const [favNames, setFavNames] = useState(new Set());
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customData, setCustomData] = useState({ name: "", calories: "", protein: "", carbs: "", fat: "", fiber: "0", sugar: "0", saturated_fat: "0" });
  const [savingCustom, setSavingCustom] = useState(false);
  // Feb 2026 — Optional "suggest for global DB" flag (admin review queue).
  const [suggestGlobal, setSuggestGlobal] = useState(false);
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [categoryFoods, setCategoryFoods] = useState({});  // { cat_id: [foods] }
  const [loadingCategory, setLoadingCategory] = useState(false);
  // iter327Q — Source filter chips (custom/local/usda_db/usda_api/supplements). Empty = show all.
  const [activeSources, setActiveSources] = useState([]);
  // iter536 — Restaurants tab data
  const [restaurantDishes, setRestaurantDishes] = useState([]);
  const [restaurantQuery, setRestaurantQuery] = useState("");
  // iter537 — Photo/PDF import modal
  const [importModalOpen, setImportModalOpen] = useState(false);
  // iter327S — Scroll the SelectedFoodPanel into view when a food is picked,
  // so users on small screens don't have to scroll up to find the macros editor.
  const selectedPanelRef = useRef(null);
  const contentScrollRef = useRef(null);
  useEffect(() => {
    if (selectedFood && selectedPanelRef.current) {
      // Scroll the modal's content container to top so the panel is visible
      if (contentScrollRef.current) {
        contentScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [selectedFood]);

  // Fetch favorites, recents, custom foods on mount
  useEffect(() => {
    const fetch = async () => {
      try {
        const [favRes, recRes, custRes] = await Promise.all([
          axios.get(`${API}/food/favorites`),
          axios.get(`${API}/food/recent`),
          axios.get(`${API}/food/custom/list`),
        ]);
        const favList = favRes.data.favorites || [];
        setFavorites(favList);
        setFavNames(new Set(favList.map(f => f.food_name)));
        setRecents(recRes.data.recents || []);
        setCustomFoods(custRes.data.foods || []);
      } catch { /* silent */ }
    };
    fetch();
  }, [API]);

  // iter536 — Fetch popular restaurant dishes when Restaurants tab opens
  useEffect(() => {
    if (activeTab !== "restaurants" || restaurantDishes.length > 0) return;
    axios.get(`${API}/restaurants/dishes/popular?limit=30`)
      .then(({ data }) => setRestaurantDishes(data.dishes || []))
      .catch(() => {});
  }, [activeTab, API, restaurantDishes.length]);

  // Fetch category foods when a non-special tab is selected
  useEffect(() => {
    const specialTabs = ["all", "favorites", "recent", "custom", "restaurants"];
    if (specialTabs.includes(activeTab)) return;
    if (categoryFoods[activeTab]) return;  // already loaded
    const loadCategory = async () => {
      setLoadingCategory(true);
      try {
        const { data } = await axios.get(`${API}/food/category/${activeTab}`, { params: { limit: 50 } });
        setCategoryFoods(prev => ({ ...prev, [activeTab]: data.foods || [] }));
      } catch { /* silent */ }
      setLoadingCategory(false);
    };
    loadCategory();
  }, [activeTab, API, categoryFoods]);

  const toggleFavorite = async (food) => {
    const foodName = food.name || food.food_name;
    try {
      const { data } = await axios.post(`${API}/food/favorite`, {
        food_name: foodName,
        food_data: food.per100g || { calories: food.calories, protein: food.protein, carbs: food.carbs, fat: food.fat, fiber: food.fiber || 0, sugar: food.sugar || 0, saturated_fat: food.saturated_fat || 0 },
      });
      if (data.status === "added") {
        setFavNames(prev => new Set([...prev, foodName]));
        toast.success(bg ? "Добавено в любими" : "Added to favorites");
      } else {
        setFavNames(prev => { const n = new Set(prev); n.delete(foodName); return n; });
        toast.success(bg ? "Премахнато от любими" : "Removed from favorites");
      }
    } catch { /* silent */ }
  };

  // iter606 — Cooked/Raw are mutually exclusive: turning one ON turns the other OFF.
  const toggleFilter = (id) => setActiveFilters(prev => {
    const has = prev.includes(id);
    if (has) return prev.filter(f => f !== id);
    let next = [...prev, id];
    if (id === "cooked_only") next = next.filter(f => f !== "raw_only");
    if (id === "raw_only") next = next.filter(f => f !== "cooked_only");
    return next;
  });

  // Build display list based on active tab
  const getDisplayItems = () => {
    if (activeTab === "favorites") {
      return favorites.map(f => ({
        name: f.food_name,
        per100g: f.food_data || {},
        source: "favorite",
      }));
    }
    if (activeTab === "recent") {
      return recents.map(r => ({
        name: r.food_name,
        per100g: { calories: r.calories, protein: r.protein, carbs: r.carbs, fat: r.fat, fiber: r.fiber || 0, sugar: r.sugar || 0, saturated_fat: r.saturated_fat || 0 },
        source: "recent",
        _lastAmount: r.amount_g,
      }));
    }
    if (activeTab === "custom") {
      return customFoods.map(c => ({
        name: c.name,
        brand: c.brand || "Custom",
        per100g: c.per100g || {},
        source: "custom",
      }));
    }
    // iter536 — Restaurants tab: convert per-portion to per100g for display compatibility
    if (activeTab === "restaurants") {
      const q = restaurantQuery.trim().toLowerCase();
      const filtered = restaurantDishes.filter((d) => {
        if (!q) return true;
        return (d.dish_name || "").toLowerCase().includes(q) ||
               (d.restaurant_name || "").toLowerCase().includes(q);
      });
      return filtered.map((d) => {
        const m = d.macros || {};
        const portion = d.portion_g || 350;
        const factor = 100 / portion;  // normalise to per-100g
        return {
          name: `${d.dish_name} · ${d.restaurant_name}`,
          brand: d.restaurant_name,
          per100g: {
            calories:      Math.round((m.kcal || 0) * factor),
            protein:       Math.round((m.protein_g || 0) * factor * 10) / 10,
            carbs:         Math.round((m.carbs_g || 0) * factor * 10) / 10,
            fat:           Math.round((m.fat_g || 0) * factor * 10) / 10,
            fiber:         Math.round((m.fiber_g || 0) * factor * 10) / 10,
            sugar:         Math.round((m.sugar_g || 0) * factor * 10) / 10,
            sodium:        Math.round((m.sodium_mg || 0) * factor),
          },
          source: "restaurant",
          _restaurantDishId: d.id,
          _portionDefault:   portion,
          _dil:              d.dil_g,
          _nis:              d.nis_percent,
          _gi:               d.gi,
          _gl:               d.gl,
          _pral:             d.pral_meq,
          _inflam:           d.inflammation_score,
          _col3:             d.col3_synthesis_score,
          _fodmap:           d.fodmap_tier,
          _description:      d.description,
        };
      });
    }
    // Category tabs: prefer pre-loaded category foods from backend
    const catTab = FOOD_CATEGORIES.find(c => c.id === activeTab);
    if (catTab && !["all", "favorites", "recent", "custom", "restaurants"].includes(activeTab)) {
      // If user is searching, filter search results by category
      if (searchQuery.length >= 2 && searchResults.length > 0) {
        const cats = {
          fruits: ["fruit"], vegetables: ["vegetable"], meat_fish: ["meat", "fish"],
          dairy: ["dairy", "eggs"], grains: ["grain"], legumes: ["legume"],
          nuts: ["nuts"], beverage: ["beverage"], spice: ["spice"], supplement: ["supplement"],
        }[activeTab] || [activeTab];
        return searchResults.filter(f => cats.includes(f.category));
      }
      // Otherwise show pre-populated category foods
      return categoryFoods[activeTab] || [];
    }
    return searchResults;
  };

  let displayItems = getDisplayItems();

  // Apply dietary filters
  if (activeFilters.length > 0) {
    displayItems = displayItems.filter(food =>
      activeFilters.every(fId => {
        const filter = DIETARY_FILTERS.find(df => df.id === fId);
        return filter ? filter.test(food) : true;
      })
    );
  }

  // iter358 — Originally hid `source: "local"` (legacy BG DB with pre-v2.0
  // DIL coefficients) and supplements. Feb 2026 — RE-ENABLE `local` for
  // search results: the user cannot find "марули" / "риба тон" without the
  // curated BG canonical rows, and the DIL concern only affected scoring
  // (not macros). Supplements remain filtered as they are mis-categorised
  // for basic macro display.
  displayItems = displayItems.filter(food => {
    const src = (food.source || "").toLowerCase();
    return src !== "supplements" && src !== "supplement";
  });

  const isSearchTab = activeTab === "all" || (!["favorites", "recent", "custom", "restaurants"].includes(activeTab) && searchQuery.length > 0);

  return createPortal(
    <div className="fixed inset-0 z-[100]" data-testid="food-search-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className={`absolute left-0 top-0 w-full h-full sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[95vw] sm:max-w-[560px] sm:max-h-[88vh] sm:h-auto sm:rounded-2xl rounded-none shadow-2xl border flex flex-col ${
        isDark ? 'bg-[hsl(222,47%,11%)] border-[hsl(223,47%,20%)]' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-[hsl(223,47%,18%)]' : 'border-gray-100'}`}>
          <div className="flex items-center space-x-2">
            {activeMeal && (
              <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${activeMeal.color}20` }}>
                {React.createElement(activeMeal.icon, { className: "w-3.5 h-3.5", style: { color: activeMeal.color } })}
              </div>
            )}
            <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>
              {bg ? `Добавяне към ${activeMeal?.labelBg}` : `Add to ${activeMeal?.labelEn}`}
            </h3>
          </div>
          <button onClick={onClose} className={`p-1 rounded-lg ${isDark ? 'hover:bg-[hsl(223,47%,18%)] text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* iter300 — Meal type selector chips (allow quick switch without closing) */}
        {setActiveMealType && Array.isArray(mealTypes) && mealTypes.length > 0 && (
          <div
            className={`flex items-center gap-1.5 px-4 py-2 border-b overflow-x-auto ${isDark ? 'border-[hsl(223,47%,18%)] bg-[hsl(223,47%,9%)]' : 'border-gray-100 bg-gray-50'}`}
            data-testid="food-overlay-meal-switcher"
          >
            <span className={`text-[10px] uppercase tracking-wider font-bold shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              {bg ? 'Добавяне към:' : 'Add to:'}
            </span>
            {mealTypes.map((mt) => {
              const Icon = mt.icon;
              const isActive = activeMealType === mt.id;
              return (
                <button
                  key={mt.id}
                  type="button"
                  onClick={() => setActiveMealType(mt.id)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors shrink-0 ${
                    isActive
                      ? 'text-white shadow-sm'
                      : (isDark ? 'bg-white/5 text-gray-300 hover:bg-white/10' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100')
                  }`}
                  style={isActive ? { backgroundColor: mt.color } : undefined}
                  data-testid={`food-overlay-meal-${mt.id}`}
                >
                  {Icon && <Icon className="w-3 h-3" />}
                  {bg ? mt.labelBg : mt.labelEn}
                </button>
              );
            })}
          </div>
        )}

        {/* Search Bar */}
        <div className={`px-4 py-2.5 border-b ${isDark ? 'border-[hsl(223,47%,18%)]' : 'border-gray-100'}`}>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <Input
                placeholder={bg ? "Търси храна..." : "Search food..."}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (e.target.value.length >= 2) {
                    setActiveTab("all");
                    clearTimeout(window.__foodSearchTimer);
                    window.__foodSearchTimer = setTimeout(() => onSearch(e.target.value), 400);
                  }
                }}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
                className={`pl-10 h-10 text-sm rounded-xl ${isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white placeholder:text-gray-500' : 'bg-gray-50 border-gray-200'}`}
                data-testid="food-search-input"
                autoFocus
              />
            </div>
            <Button onClick={onSearch} disabled={searching} className="h-10 px-4 gradient-primary text-white rounded-xl" data-testid="food-search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          {translatedQuery && (
            <p className={`text-[10px] mt-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {bg ? "Търсене и за" : "Also searching"}: <span className="font-medium text-[#0EA5E9]">&ldquo;{translatedQuery}&rdquo;</span>
            </p>
          )}
        </div>

        {/* Category Tabs — Cronometer-style (wraps to multiple rows so all 14 tabs stay visible) */}
        <div className={`px-3 py-2 border-b flex flex-wrap gap-1 ${isDark ? 'border-[hsl(223,47%,18%)]' : 'border-gray-100'}`} data-testid="food-category-tabs">
          {FOOD_CATEGORIES.map(cat => {
            const isActive = activeTab === cat.id;
            const Icon = cat.icon;
            const count = cat.id === "favorites" ? favorites.length : cat.id === "recent" ? recents.length : cat.id === "custom" ? customFoods.length : null;
            return (
              <button key={cat.id} onClick={() => {
                setActiveTab(cat.id);
                if (["favorites", "recent", "custom"].includes(cat.id)) { setSearchQuery(""); }
              }}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                  isActive
                    ? isDark ? 'bg-[#0EA5E9]/15 text-[#0EA5E9] ring-1 ring-[#0EA5E9]/30' : 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                    : isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-[hsl(223,47%,15%)]' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
                data-testid={`tab-${cat.id}`}
              >
                {Icon && <Icon className="w-3 h-3" style={cat.color && isActive ? { color: cat.color } : {}} />}
                {cat.emoji && <span className="text-xs">{cat.emoji}</span>}
                {bg ? cat.label : cat.labelEn}
                {count != null && count > 0 && <span className={`ml-0.5 text-[10px] ${isActive ? '' : 'opacity-60'}`}>({count})</span>}
              </button>
            );
          })}
        </div>

        {/* Dietary Filters — only when searching */}
        {isSearchTab && searchResults.length > 0 && (
          <div className={`px-4 py-1.5 flex gap-1 flex-wrap ${isDark ? '' : ''}`} data-testid="dietary-filters">
            {DIETARY_FILTERS.map(f => {
              const active = activeFilters.includes(f.id);
              return (
                <button key={f.id} onClick={() => toggleFilter(f.id)}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-all border ${
                    active ? "text-white border-transparent" : isDark ? "border-[hsl(223,47%,20%)] text-gray-500" : "border-gray-200 text-gray-400"
                  }`}
                  style={active ? { backgroundColor: f.color, borderColor: f.color } : {}}
                  data-testid={`filter-${f.id}`}
                >
                  {bg ? f.labelBg : f.labelEn}
                </button>
              );
            })}
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" ref={contentScrollRef}>
          {selectedFood && (
            <div ref={selectedPanelRef}>
              <SelectedFoodPanel
                food={selectedFood} isDark={isDark} bg={bg} language={language}
                amount={amount} setAmount={setAmount}
                adding={adding} onAdd={onAddFood}
                onDeselect={() => setSelectedFood(null)}
                isFavorite={favNames.has(selectedFood.name || selectedFood.food_name)}
                onToggleFavorite={() => toggleFavorite(selectedFood)}
                logTime={logTime}
                setLogTime={setLogTime}
              />
            </div>
          )}

          <div className={`${selectedFood ? 'px-3 pb-3' : 'p-3'}`}>
            {/* Loading */}
            {(searching || loadingCategory) && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className={`w-5 h-5 animate-spin ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <span className={`ml-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{bg ? "Търсене..." : "Searching..."}</span>
              </div>
            )}

            {/* Empty states */}
            {!searching && !loadingCategory && displayItems.length === 0 && (
              <div className="text-center py-6">
                {activeTab === "favorites" ? (
                  <>
                    <Heart className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{bg ? "Няма любими храни. Натиснете ★ до храна за да я добавите." : "No favorites. Tap ★ on a food to add it."}</p>
                  </>
                ) : activeTab === "recent" ? (
                  <>
                    <Clock className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{bg ? "Няма скорошни храни. Добавете храна към дневника." : "No recent foods. Add food to your diary."}</p>
                  </>
                ) : searchQuery.length > 0 ? (
                  <>
                    <Search className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                    <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{bg ? "Няма резултати." : "No results."}</p>
                    <Button variant="outline" size="sm" onClick={() => { setShowCustomForm(true); setCustomData(prev => ({...prev, name: searchQuery})); }} className="text-[10px]" data-testid="add-custom-food-btn">
                      <PlusCircle className="w-3 h-3 mr-1" />{bg ? "Добавяне на моя храна" : "Add custom food"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Search className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{bg ? "Въведете име на храна" : "Enter a food name"}</p>
                  </>
                )}
              </div>
            )}

            {/* Results list */}
            {displayItems.length > 0 && (() => {
              // iter327Q — Source filter chips
              const allSources = [...new Set(displayItems.map((d) => d.source).filter(Boolean))];
              const filteredItems = activeSources.length === 0
                ? displayItems
                : displayItems.filter((d) => activeSources.includes(d.source));
              return (
              <div className="space-y-0.5">
                {/* Source filter row */}
                {allSources.length > 1 && (
                  <div className="flex items-center gap-1 mb-2 flex-wrap" data-testid="source-filter-row">
                    <span className={`text-[9px] uppercase tracking-wider mr-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {bg ? "Източник:" : "Source:"}
                    </span>
                    <button
                      onClick={() => setActiveSources([])}
                      data-testid="source-filter-all"
                      className={`text-[9px] px-2 py-0.5 rounded-full font-semibold transition-all ${
                        activeSources.length === 0
                          ? 'bg-[#0EA5E9] text-white'
                          : isDark ? 'bg-[hsl(223,47%,15%)] text-gray-400' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {bg ? "Всички" : "All"}
                    </button>
                    {allSources.map((src) => {
                      const labels = {
                        custom: bg ? "Моите" : "Mine",
                        nutrami: "Nutrami",
                        bg_canonical: bg ? "БГ Курирана" : "BG Curated",
                        local: bg ? "БГ База" : "BG DB",
                        usda_db: "USDA",
                        usda_api: "USDA+",
                        supplements: bg ? "Добавки" : "Supplements",
                        favorite: bg ? "Любими" : "Favorites",
                        recent: bg ? "Последни" : "Recent",
                      };
                      const colors = {
                        custom: 'bg-violet-500',
                        nutrami: 'bg-emerald-600',
                        bg_canonical: 'bg-emerald-600',
                        local: 'bg-emerald-500', usda_db: 'bg-blue-500',
                        usda_api: 'bg-blue-400', supplements: 'bg-pink-500',
                        favorite: 'bg-amber-500', recent: 'bg-gray-500',
                      };
                      const isActive = activeSources.includes(src);
                      return (
                        <button
                          key={src}
                          onClick={() => setActiveSources(p => isActive ? p.filter(s => s !== src) : [...p, src])}
                          data-testid={`source-filter-${src}`}
                          className={`text-[9px] px-2 py-0.5 rounded-full font-semibold transition-all ${
                            isActive
                              ? `${colors[src] || 'bg-gray-500'} text-white`
                              : isDark ? 'bg-[hsl(223,47%,15%)] text-gray-400' : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {labels[src] || src}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-center justify-between mb-1.5">
                  <p className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                    {filteredItems.length} {bg ? "резултата" : "results"}
                  </p>
                  <div className="flex gap-2">
                    {/* iter537 — Photo/PDF AI import (Restaurants tab only) */}
                    {activeTab === "restaurants" && (
                      <button
                        onClick={() => setImportModalOpen(true)}
                        data-testid="restaurant-import-open-btn"
                        className="text-[10px] text-emerald-500 font-medium hover:underline"
                      >
                        📷 {bg ? "Внасяне от снимка" : "Import from photo"}
                      </button>
                    )}
                    <button onClick={() => setShowCustomForm(true)} className="text-[10px] text-[#0EA5E9] font-medium hover:underline" data-testid="add-custom-food-btn">
                      <PlusCircle className="w-3 h-3 inline mr-0.5" />{bg ? "Моя храна" : "Custom"}
                    </button>
                  </div>
                </div>
                {filteredItems.map((food, i) => {
                  const p = food.per100g || {};
                  const foodName = food.name || food.food_name;
                  const isFav = favNames.has(foodName);
                  const isSupplement = food.source === "supplements";
                  const supInfo = food.supplement_info || {};
                  return (
                    <div key={`${foodName}-${i}`}
                      className={`flex items-center gap-2 p-2.5 rounded-xl transition-all ${
                        selectedFood?.name === foodName
                          ? isDark ? 'bg-[hsl(223,47%,18%)] ring-1 ring-[#0EA5E9]/50' : 'bg-[#F0F9FF] ring-1 ring-[#0EA5E9]/30'
                          : isDark ? 'hover:bg-[hsl(223,47%,15%)]' : 'hover:bg-gray-50'
                      }`}
                      data-testid={`food-result-${i}`}
                    >
                      {/* Favorite star */}
                      <button onClick={(e) => { e.stopPropagation(); toggleFavorite(food); }}
                        className="flex-shrink-0 p-0.5" data-testid={`fav-${i}`}>
                        <Star className={`w-3.5 h-3.5 transition-colors ${isFav ? 'text-[#F59E0B] fill-[#F59E0B]' : isDark ? 'text-gray-700 hover:text-gray-500' : 'text-gray-200 hover:text-gray-400'}`} />
                      </button>

                      {/* Supplement icon (emoji) when applicable */}
                      {isSupplement && supInfo.icon && (
                        <span className="text-base flex-shrink-0" aria-hidden>{supInfo.icon}</span>
                      )}

                      {/* Food info — clickable */}
                      <button className="flex-1 text-left min-w-0" onClick={() => { setSelectedFood(food); setAmount(food._lastAmount ? String(food._lastAmount) : (isSupplement ? "1" : "100")); }}>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <p
                            className={`text-xs font-medium truncate min-w-0 ${isDark ? 'text-gray-200' : 'text-[#1A1A2E]'}`}
                            title={food.source === "nutrami" ? _pickNutramiName(food, language) : translateFoodName(foodName, language)}
                          >
                            {/* iter965 — Nutrami rows carry curated BG + EN
                                names. Pick the one matching the user's active
                                locale so English/ES visitors don't get stuck
                                with a Cyrillic-only label. */}
                            {food.source === "nutrami"
                              ? _pickNutramiName(food, language)
                              : translateFoodName(foodName, language)}
                          </p>
                          <SourceBadge source={food.source} bg={bg} />
                          <FoodReportButton
                            foodId={food.id || (food.fdc_id ? `usda-${food.fdc_id}` : null)}
                            foodLayer={food.source === "nutrami" ? "nutrami"
                                     : (food.source === "usda_db" || food.source === "usda_api") ? "usda"
                                     : null}
                            foodName={food.source === "nutrami" ? _pickNutramiName(food, language) : translateFoodName(foodName, language)}
                            bg={bg}
                          />
                          {/* iter327R — Dry/dehydrated/powder advisory: protein values look very high because of removed water */}
                          {food.dry_concentrate && (
                            <span
                              className="inline-flex items-center px-1 py-px rounded text-[8px] font-semibold flex-shrink-0 bg-amber-500/15 text-amber-600 ring-1 ring-amber-500/30"
                              title={bg ? "Сушено / концентрат — стойностите са на сухо тегло" : "Dried / concentrate — values per dry weight"}
                              data-testid={`dry-concentrate-badge-${i}`}
                            >
                              ⚠ {bg ? "сухо" : "dry"}
                            </span>
                          )}
                        </div>
                        {/* iter967 — English/USDA subtitle for BG-curated rows
                            and vice-versa. Helps a foreign visitor recognise
                            the food when the primary label is in Cyrillic. */}
                        {(() => {
                          const primary = food.source === "nutrami"
                            ? _pickNutramiName(food, language)
                            : translateFoodName(foodName, language);
                          const sub = _foodSubtitle(food, language, primary);
                          if (!sub) return null;
                          return (
                            <div className="mt-0.5">
                              <p
                                className={`text-[10px] truncate ${isDark ? "text-gray-500" : "text-gray-500"}`}
                                title={sub}
                                data-testid={`food-subtitle-${i}`}
                              >
                                {sub}
                              </p>
                              {/* iter971 — "Verify on USDA FoodData Central" link.
                                  Only rendered for Nutrami rows carrying a
                                  `source_fdc_id` (all 46 curated rows do).
                                  Positions Nutramilo as a curated USDA-verified
                                  layer rather than a closed proprietary DB. */}
                              {food.source === "nutrami" && food.source_fdc_id && (
                                <a
                                  href={`https://fdc.nal.usda.gov/food-details/${food.source_fdc_id}/nutrients`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className={`hidden sm:inline-flex items-center gap-0.5 text-[9px] font-medium mt-0.5 rounded px-1 py-px transition ${
                                    isDark
                                      ? "text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10"
                                      : "text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                                  }`}
                                  title={bg
                                    ? "Отвори официалната USDA FoodData Central страница за този запис"
                                    : "Open the official USDA FoodData Central page for this record"}
                                  data-testid={`usda-verify-link-${i}`}
                                >
                                  🔗 {bg ? "Провери в USDA FoodData Central" : "Verify on USDA FoodData Central"}
                                </a>
                              )}
                            </div>
                          );
                        })()}
                        {/* iter327Q — supplements show dosage / form, NOT calories+macros */}
                        {isSupplement ? (
                          <div className={`flex gap-2 mt-0.5 text-[10px] flex-wrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            <span className="font-bold text-pink-500">{supInfo.dosage || food.serving}</span>
                            {supInfo.form && <span>· {supInfo.form}</span>}
                            {food.brand && <span className="italic">· {food.brand}</span>}
                          </div>
                        ) : (
                          <div className={`flex gap-2 mt-0.5 text-[10px] flex-wrap ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {p.dil_100g != null && <span className="font-bold text-[#EF4444]">DIL {p.dil_100g}</span>}
                            {p.dil_100g == null && p.protein != null && <span className="font-bold text-[#EF4444]">DIL {computeDil(p.protein || 0, p.carbs || 0, p.fiber || 0).toFixed(1)}</span>}
                            <span className="font-medium">{p.calories} kcal</span>
                            <span className="text-[#10B981]">P:{p.protein}g</span>
                            <span className="text-[#F59E0B]">C:{p.carbs}g</span>
                            <span className="text-[#6366F1]">F:{p.fat}g</span>
                            {/* iter964 — MDI + SI pills alongside macros */}
                            {p.mdi_percent != null && (
                              <span
                                className="font-bold flex items-center gap-0.5"
                                style={{ color: p.mdi_tier_color || (p.mdi_percent >= 60 ? "#10B981" : p.mdi_percent >= 30 ? "#F59E0B" : "#9CA3AF") }}
                                title={language==='bg' ? "MDI · Плътност на микронутриенти" : "MDI · Micronutrient density"}
                                data-testid={`food-search-mdi-${food.fdc_id || food.id || food.name}`}
                              >
                                <span className="w-1 h-1 rounded-full inline-block" style={{ background: p.mdi_tier_color || (p.mdi_percent >= 60 ? "#10B981" : p.mdi_percent >= 30 ? "#F59E0B" : "#9CA3AF") }} />
                                MDI {Math.round(p.mdi_percent)}
                              </span>
                            )}
                            {p.satiety_index != null && (
                              <span
                                className="font-bold flex items-center gap-0.5"
                                style={{ color: p.satiety_color || (p.satiety_index >= 130 ? "#10B981" : p.satiety_index >= 80 ? "#F59E0B" : "#EF4444") }}
                                title={language==='bg' ? "SI · Индекс на засищане" : "SI · Satiety Index"}
                                data-testid={`food-search-si-${food.fdc_id || food.id || food.name}`}
                              >
                                <span className="w-1 h-1 rounded-full inline-block" style={{ background: p.satiety_color || (p.satiety_index >= 130 ? "#10B981" : p.satiety_index >= 80 ? "#F59E0B" : "#EF4444") }} />
                                SI {Math.round(p.satiety_index)}
                              </span>
                            )}
                          </div>
                        )}
                        {/* iter536 — Extra restaurant-dish indices badge row */}
                        {food.source === "restaurant" && (
                          <div className={`flex gap-1 mt-1 flex-wrap text-[9px]`}>
                            {food._nis != null && (
                              <span className="px-1 py-px rounded bg-rose-500/15 text-rose-500 font-semibold">NIS {food._nis}%</span>
                            )}
                            {food._gi != null && (
                              <span className="px-1 py-px rounded bg-amber-500/15 text-amber-600 font-semibold">GI {food._gi}</span>
                            )}
                            {food._gl != null && (
                              <span className="px-1 py-px rounded bg-orange-500/15 text-orange-600 font-semibold">GL {food._gl}</span>
                            )}
                            {food._pral != null && (
                              <span className="px-1 py-px rounded bg-purple-500/15 text-purple-500 font-semibold">PRAL {food._pral}</span>
                            )}
                            {food._inflam != null && (
                              <span className="px-1 py-px rounded bg-red-500/15 text-red-600 font-semibold">🔥{food._inflam}/10</span>
                            )}
                            {food._col3 != null && (
                              <span className="px-1 py-px rounded bg-emerald-500/15 text-emerald-600 font-semibold">COL3 {food._col3}/10</span>
                            )}
                            {food._fodmap && (
                              <span className={`px-1 py-px rounded font-semibold ${
                                food._fodmap === "low"  ? "bg-green-500/15 text-green-600"
                                : food._fodmap === "high" ? "bg-red-500/15 text-red-600"
                                : "bg-yellow-500/15 text-yellow-600"}`}>
                                FODMAP:{food._fodmap}
                              </span>
                            )}
                          </div>
                        )}
                      </button>

                      <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-gray-700' : 'text-gray-300'}`} />
                    </div>
                  );
                })}
              </div>
              );
            })()}
          </div>
        </div>

        {/* Custom Food Form */}
        {showCustomForm && (
          <div className={`absolute inset-0 z-10 flex items-center justify-center ${isDark ? 'bg-black/70' : 'bg-black/40'}`}>
            <div className={`w-full max-w-sm mx-4 rounded-2xl p-5 ${isDark ? 'bg-[hsl(222,47%,11%)] border border-[hsl(223,47%,20%)]' : 'bg-white shadow-xl'}`} data-testid="custom-food-form">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>{bg ? "Добавяне на моя храна" : "Add Custom Food"}</h3>
                <button onClick={() => setShowCustomForm(false)} className="p-1"><X className="w-4 h-4 text-gray-400" /></button>
              </div>
              <div className="space-y-2">
                <div>
                  <label className={`text-[10px] font-semibold uppercase tracking-wider mb-1 block ${isDark ? 'text-gray-300' : 'text-gray-600'}`} htmlFor="cf-name">{bg ? "Име на храната" : "Food name"} *</label>
                  <Input id="cf-name" placeholder={bg ? "напр. Зеленчуков бульон" : "e.g. Vegetable broth"} value={customData.name} onChange={e => setCustomData(p => ({...p, name: e.target.value}))} className={`h-9 text-sm ${isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white' : ''}`} data-testid="custom-food-name" />
                </div>
                <p className={`text-[10px] font-semibold uppercase tracking-wider pt-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{bg ? "Хранителна стойност (на 100g)" : "Nutrition (per 100g)"}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={`text-[9px] mb-0.5 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`} htmlFor="cf-cal">{bg ? "Калории" : "Calories"} (kcal) *</label>
                    <Input id="cf-cal" placeholder="0" type="number" value={customData.calories} onChange={e => setCustomData(p => ({...p, calories: e.target.value}))} className={`h-8 text-xs ${isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white' : ''}`} data-testid="custom-food-calories" />
                  </div>
                  <div>
                    <label className={`text-[9px] mb-0.5 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`} htmlFor="cf-prot">{bg ? "Протеин" : "Protein"} (g)</label>
                    <Input id="cf-prot" placeholder="0" type="number" value={customData.protein} onChange={e => setCustomData(p => ({...p, protein: e.target.value}))} className={`h-8 text-xs ${isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white' : ''}`} data-testid="custom-food-protein" />
                  </div>
                  <div>
                    <label className={`text-[9px] mb-0.5 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`} htmlFor="cf-carbs">{bg ? "Въглехидрати" : "Carbs"} (g)</label>
                    <Input id="cf-carbs" placeholder="0" type="number" value={customData.carbs} onChange={e => setCustomData(p => ({...p, carbs: e.target.value}))} className={`h-8 text-xs ${isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white' : ''}`} data-testid="custom-food-carbs" />
                  </div>
                  <div>
                    <label className={`text-[9px] mb-0.5 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`} htmlFor="cf-fat">{bg ? "Мазнини" : "Fat"} (g)</label>
                    <Input id="cf-fat" placeholder="0" type="number" value={customData.fat} onChange={e => setCustomData(p => ({...p, fat: e.target.value}))} className={`h-8 text-xs ${isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white' : ''}`} data-testid="custom-food-fat" />
                  </div>
                  <div>
                    <label className={`text-[9px] mb-0.5 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`} htmlFor="cf-fiber">{bg ? "Фибри" : "Fiber"} (g)</label>
                    <Input id="cf-fiber" placeholder="0" type="number" value={customData.fiber} onChange={e => setCustomData(p => ({...p, fiber: e.target.value}))} className={`h-8 text-xs ${isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white' : ''}`} data-testid="custom-food-fiber" />
                  </div>
                  <div>
                    <label className={`text-[9px] mb-0.5 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`} htmlFor="cf-sugar">{bg ? "Захари" : "Sugar"} (g)</label>
                    <Input id="cf-sugar" placeholder="0" type="number" value={customData.sugar} onChange={e => setCustomData(p => ({...p, sugar: e.target.value}))} className={`h-8 text-xs ${isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white' : ''}`} data-testid="custom-food-sugar" />
                  </div>
                </div>
                <p className={`text-[9px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{bg ? "Полетата отбелязани с * са задължителни" : "Fields marked * are required"}</p>

                {/* Feb 2026 — Suggest for global DB (admin review) */}
                <label className={`flex items-start gap-2 mt-1 p-2 rounded-lg cursor-pointer transition-colors ${isDark ? 'bg-[hsl(223,47%,15%)] hover:bg-[hsl(223,47%,17%)]' : 'bg-gray-50 hover:bg-gray-100'}`} data-testid="suggest-global-label">
                  <input
                    type="checkbox"
                    checked={suggestGlobal}
                    onChange={e => setSuggestGlobal(e.target.checked)}
                    className="mt-0.5 rounded"
                    data-testid="suggest-global-checkbox"
                  />
                  <span className={`text-[10px] leading-tight ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {bg
                      ? "Предложи този продукт да бъде добавен в общата база (ще бъде прегледан от админ)."
                      : "Suggest this food for the global database (admin will review)."}
                  </span>
                </label>

                <Button disabled={!customData.name || !customData.calories || savingCustom || submittingSuggestion}
                  onClick={async () => {
                    setSavingCustom(true);
                    let addOk = false;
                    try {
                      await onAddCustomFood(customData);
                      addOk = true;
                    } catch (err) {
                      console.error("[custom-food] onAddCustomFood failed", err);
                      toast.error(bg ? "Грешка при запазването на храната." : "Failed to save custom food.");
                    }
                    // Feb 2026 — Fire the global-suggestion POST independently
                    // of the local save so the two failure modes don't couple.
                    if (suggestGlobal) {
                      try {
                        setSubmittingSuggestion(true);
                        await axios.post(`${API}/food/suggest`, {
                          name_bg: customData.name,
                          per100g: {
                            calories: Number(customData.calories) || 0,
                            protein: Number(customData.protein) || 0,
                            carbs: Number(customData.carbs) || 0,
                            fat: Number(customData.fat) || 0,
                            fiber: Number(customData.fiber) || 0,
                            sugar: Number(customData.sugar) || 0,
                            sodium_mg: 0,
                          },
                        });
                        toast.success(bg ? "Предложението ви е получено. Ще бъде прегледано от админ." : "Your suggestion is queued for admin review.");
                      } catch (err) {
                        const detail = err?.response?.data?.detail || (bg ? "Грешка при изпращане на предложението." : "Suggestion could not be submitted.");
                        toast.error(detail);
                      } finally {
                        setSubmittingSuggestion(false);
                      }
                    }
                    if (addOk) {
                      setShowCustomForm(false);
                      setSuggestGlobal(false);
                      setCustomData({ name: "", calories: "", protein: "", carbs: "", fat: "", fiber: "0", sugar: "0", saturated_fat: "0" });
                    }
                    setSavingCustom(false);
                  }}
                  className="w-full h-9 gradient-primary text-white rounded-xl text-xs" data-testid="save-custom-food-btn">
                  {(savingCustom || submittingSuggestion) ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                  {bg ? "Запази" : "Save"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* iter537 — Restaurant import modal */}
      <RestaurantImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImported={() => {
          // Refetch popular dishes after successful import
          setRestaurantDishes([]);
        }}
        isDark={isDark}
      />
    </div>,
    document.body
  );
}

function SelectedFoodPanel({ food, isDark, bg, language, amount, setAmount, adding, onAdd, onDeselect, isFavorite, onToggleFavorite, logTime, setLogTime }) {
  const { API } = useAuth();
  const [showDetail, setShowDetail] = useState(false);
  const [nutrientsDetail, setNutrientsDetail] = useState(food.nutrients || null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const mult = (parseFloat(amount) || 0) / 100;
  const p = food.per100g || {};
  const scaled = {
    calories: Math.round((p.calories || 0) * mult),
    protein: ((p.protein || 0) * mult).toFixed(1),
    carbs: ((p.carbs || 0) * mult).toFixed(1),
    fat: ((p.fat || 0) * mult).toFixed(1),
    fiber: ((p.fiber || 0) * mult).toFixed(1),
    sugar: ((p.sugar || 0) * mult).toFixed(1),
    saturated_fat: ((p.saturated_fat || 0) * mult).toFixed(1),
  };

  return (
    <div className={`m-3 p-3.5 rounded-xl border-2 ${isDark ? 'bg-[hsl(223,47%,14%)] border-[#0EA5E9]/40' : 'bg-[#F0F9FF] border-[#0EA5E9]/30'}`} data-testid="selected-food-panel">
      <div className="flex items-start justify-between mb-2.5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>
              {food.source === "nutrami"
                ? _pickNutramiName(food, language)
                : translateFoodName(food.name || food.food_name, language)}
            </p>
            <SourceBadge source={food.source} bg={bg} />
          </div>
          {/* iter967 — English/USDA subtitle on the selected-food panel */}
          {(() => {
            const primary = food.source === "nutrami"
              ? _pickNutramiName(food, language)
              : translateFoodName(food.name || food.food_name, language);
            const sub = _foodSubtitle(food, language, primary);
            if (!sub) return null;
            return (
              <div className="mt-0.5">
                <p
                  className={`text-[11px] italic truncate ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  title={sub}
                  data-testid="selected-food-subtitle"
                >
                  {sub}
                </p>
                {/* iter971 — USDA FDC verification link */}
                {food.source === "nutrami" && food.source_fdc_id && (
                  <a
                    href={`https://fdc.nal.usda.gov/food-details/${food.source_fdc_id}/nutrients`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-1 text-[10px] font-medium mt-0.5 rounded px-1.5 py-0.5 transition ${
                      isDark
                        ? "text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10"
                        : "text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                    }`}
                    title={bg
                      ? "Отвори официалната USDA FoodData Central страница за този запис"
                      : "Open the official USDA FoodData Central page for this record"}
                    data-testid="selected-food-usda-verify-link"
                  >
                    🔗 {bg ? "Провери в USDA FoodData Central" : "Verify on USDA FoodData Central"}
                  </a>
                )}
              </div>
            );
          })()}
          {food.brand && <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{food.brand}</p>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onToggleFavorite} className="p-1" data-testid="toggle-fav-selected">
            <Star className={`w-4 h-4 ${isFavorite ? 'text-[#F59E0B] fill-[#F59E0B]' : isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          </button>
          <button onClick={onDeselect} className={`p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}>
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* iter327Q — Supplement display: dose + form (NOT calories/macros) */}
      {food.source === "supplements" ? (
        <>
          <div className="flex items-center gap-2 mb-2.5">
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={`w-20 h-11 sm:w-16 sm:h-9 text-center text-base sm:text-sm font-semibold ${isDark ? 'bg-[hsl(222,47%,11%)] border-[hsl(223,47%,20%)] text-white' : 'bg-white'}`}
              data-testid="food-amount-input"
              min="1"
            />
            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {bg ? "доза(и)" : "dose(s)"}
            </span>
            <div className="flex gap-1 ml-1">
              {[1, 2, 3].map(n => (
                <button key={n} onClick={() => setAmount(String(n))}
                  className={`px-2 py-1 text-[10px] rounded-md ${amount === String(n) ? 'bg-pink-500 text-white' : isDark ? 'bg-[hsl(222,47%,11%)] text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                  ×{n}
                </button>
              ))}
            </div>
          </div>

          {/* Dosage badge */}
          <div className={`mb-2 p-3 rounded-lg flex items-center gap-3 ${isDark ? 'bg-pink-950/30 border border-pink-900/40' : 'bg-pink-50 border border-pink-200'}`} data-testid="supplement-dose-display">
            {food.supplement_info?.icon && <span className="text-2xl">{food.supplement_info.icon}</span>}
            <div className="flex-1">
              <div className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-pink-300' : 'text-pink-700'}`}>
                {bg ? "Активна доза" : "Active dose"}
              </div>
              <div className="text-base font-bold text-pink-500">
                {(parseInt(amount, 10) || 1)} × {food.supplement_info?.dosage || food.serving}
              </div>
              {food.supplement_info?.form && (
                <div className={`text-[10px] mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {bg ? `Форма: ${food.supplement_info.form}` : `Form: ${food.supplement_info.form}`}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Amount + quick picks (regular food) */}
          <div className="flex items-center gap-2 mb-2.5">
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              className={`w-20 h-11 sm:w-16 sm:h-9 text-center text-base sm:text-sm font-semibold ${isDark ? 'bg-[hsl(222,47%,11%)] border-[hsl(223,47%,20%)] text-white' : 'bg-white'}`}
              data-testid="food-amount-input" />
            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>g</span>
            <div className="flex gap-1 ml-1">
              {[50, 100, 150, 200].map(g => (
                <button key={g} onClick={() => setAmount(String(g))}
                  className={`px-2 py-1 text-[10px] rounded-md ${amount === String(g) ? 'bg-[#0EA5E9] text-white' : isDark ? 'bg-[hsl(222,47%,11%)] text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                  {g}g
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* iter327N — Time of consumption (for Circadian DIL + Chrononutrition Score) */}
      {setLogTime && (
        <div className={`flex items-center gap-2 mb-2.5 px-2.5 py-2 rounded-lg ${isDark ? 'bg-[hsl(222,47%,11%)]' : 'bg-white border border-gray-200'}`}>
          <Clock className={`w-3.5 h-3.5 ${isDark ? 'text-fuchsia-400' : 'text-fuchsia-600'}`} />
          <label htmlFor="meal-log-time" className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {bg ? "Време на хранене" : "Meal time"}
          </label>
          <Input
            id="meal-log-time"
            type="time"
            value={logTime || ""}
            onChange={(e) => setLogTime(e.target.value)}
            className={`flex-1 h-7 text-xs text-center ${isDark ? 'bg-[hsl(222,47%,15%)] border-[hsl(223,47%,20%)] text-white' : 'bg-gray-50'}`}
            data-testid="meal-log-time-input"
          />
          <button
            type="button"
            onClick={() => {
              const d = new Date();
              setLogTime(`${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`);
            }}
            className={`text-[9px] px-2 py-1 rounded-md font-semibold ${isDark ? 'bg-fuchsia-900/40 text-fuchsia-200' : 'bg-fuchsia-100 text-fuchsia-700'} hover:opacity-80`}
            data-testid="meal-log-time-now"
          >
            {bg ? "Сега" : "Now"}
          </button>
        </div>
      )}

      {/* Macro grid (hidden for supplements — they have zero macros) */}
      {food.source !== "supplements" && (
        <div className={`grid grid-cols-4 gap-1 mb-2 p-2 rounded-lg ${isDark ? 'bg-[hsl(222,47%,11%)]' : 'bg-white'}`}>
          {[
            { label: "kcal", value: scaled.calories, color: isDark ? '#fff' : '#1A1A2E' },
            { label: bg ? "Прот" : "Prot", value: `${scaled.protein}g`, color: "#10B981" },
            { label: bg ? "Въгл" : "Carbs", value: `${scaled.carbs}g`, color: "#F59E0B" },
            { label: bg ? "Мазн" : "Fat", value: `${scaled.fat}g`, color: "#6366F1" },
          ].map(m => (
            <div key={m.label} className="text-center">
              <p className={`text-[9px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{m.label}</p>
              <p className="text-sm font-bold" style={{ color: m.color }}>{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* iter524 — Mineral profile for plain waters (Ca, Mg, K, Na, HCO3).
          Surfaces meaningful info for 0-calorie drinking water. */}
      {food.minerals && (
        <div
          data-testid="food-minerals-card"
          className={`mb-2 p-2.5 rounded-lg border ${isDark ? 'bg-sky-500/5 border-sky-500/20' : 'bg-sky-50 border-sky-200'}`}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? 'text-sky-300' : 'text-sky-700'}`}>
              {bg ? "Минерален профил" : "Mineral profile"}
            </span>
            <span className={`text-[9px] ${isDark ? 'text-sky-300/70' : 'text-sky-600/80'}`}>
              {bg ? food.minerals.label_bg : food.minerals.label_en}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-1 text-center">
            {[
              { label: "Ca",   value: food.minerals.calcium_mg },
              { label: "Mg",   value: food.minerals.magnesium_mg },
              { label: "K",    value: food.minerals.potassium_mg },
              { label: "Na",   value: food.minerals.sodium_mg },
            ].map((m) => (
              <div key={m.label}>
                <p className={`text-[9px] ${isDark ? 'text-sky-400/60' : 'text-sky-500/70'}`}>{m.label}</p>
                <p className={`text-[12px] font-bold ${isDark ? 'text-sky-200' : 'text-sky-800'}`}>
                  {Math.round((m.value || 0) * mult)}<span className="text-[9px] opacity-60">mg</span>
                </p>
              </div>
            ))}
          </div>
          <div className={`mt-1 text-[9px] text-center ${isDark ? 'text-sky-400/60' : 'text-sky-600/70'}`}>
            {bg ? "≈ за " : "≈ for "}{Math.round((parseFloat(amount) || 100))}ml
            {food.minerals.bicarbonate_mg > 0 && (
              <> · HCO₃ <b>{Math.round(food.minerals.bicarbonate_mg * mult)}mg</b></>
            )}
          </div>
        </div>
      )}

      <Button onClick={onAdd} disabled={adding} className={`w-full h-10 ${food.source === "supplements" ? 'bg-pink-500 hover:bg-pink-600' : 'gradient-primary'} text-white rounded-xl text-sm font-medium`} data-testid="confirm-add-food-btn">
        {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
        {food.source === "supplements"
          ? (bg ? `Добавяне на добавката` : `Add supplement`)
          : (bg ? `Добавяне (${scaled.calories} kcal)` : `Add ${scaled.calories} kcal`)}
      </Button>

      {/* iter420.2 — save searched food to Health Intelligence favorites */}
      <SaveFoodToHIButton food={food} scaled={scaled} bg={bg} isDark={isDark} />

      {/* Full nutrient detail toggle + panel */}
      {(food.nutrients || food.fdc_id) && (
        <div className="mt-2">
          <button
            onClick={async () => {
              if (showDetail) { setShowDetail(false); return; }
              if (!nutrientsDetail && food.fdc_id) {
                setLoadingDetail(true);
                try {
                  const { data } = await axios.get(`${API}/food/detail/${food.fdc_id}`);
                  setNutrientsDetail(data.nutrients || {});
                } catch { /* silent */ }
                setLoadingDetail(false);
              }
              setShowDetail(true);
            }}
            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium ${
              isDark ? 'bg-[hsl(222,47%,11%)] hover:bg-[hsl(223,47%,14%)] text-gray-300' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
            }`}
            data-testid="toggle-food-detail-btn"
          >
            <span className="flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5 text-[#6366F1]" />
              {showDetail ? (bg ? "Скрий нутриенти" : "Hide nutrients") : (bg ? "Виж всички нутриенти" : "View all nutrients")}
            </span>
            {loadingDetail ? <Loader2 className="w-3 h-3 animate-spin" /> : (
              <span className={`text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {showDetail ? "▲" : "▼"}
              </span>
            )}
          </button>
          {showDetail && (
            <div className="mt-2" data-testid="food-detail-wrapper">
              <FoodDetailPanel
                nutrients={nutrientsDetail}
                amount_g={Number(amount) || 100}
                source={food.source}
                showPriorityOnly={true}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
